import React from "react";

import {
  Edit as EditIcon,
  Info as InfoIcon,
  MoreHoriz as MoreHorizIcon,
  RemoveCircle as RemoveCircleIcon,
} from "@mui/icons-material";
import ContentCopyRoundedIcon from "@mui/icons-material/ContentCopyRounded";
import { Box, IconButton, Menu, MenuItem, Typography } from "@mui/material";
import { makeStyles } from "tss-react/mui";

import useGlobalSnackbar from "@/hooks/useGlobalSnackbar";
import { colors } from "@/theme/overrides/colors";

const useStyles = makeStyles()({
  icon: {
    color: colors.gray,
    marginRight: 8,
    width: 20,
    height: 20,
  },
  text: {
    fontWeight: 600,
    color: "#949BA8",
  },
  red: {
    color: "#FF6262",
  },
  menuItem: {
    marginTop: 4,
  },
});

type OptionMenuProps = {
  onClickMenu: (type: string) => void;
  isAppointmentActive?: boolean;
  isOnlineAppointment?: boolean;
  meetingLink?: string;
};

const OptionMenu: React.FC<OptionMenuProps> = ({
  onClickMenu,
  isAppointmentActive = true,
  isOnlineAppointment,
  meetingLink,
}) => {
  const snackbar = useGlobalSnackbar();
  const { classes } = useStyles();
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const handleOpen = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  function handleOnclick(type: string) {
    onClickMenu(type);
    handleClose();
  }

  const copyMeetingLink = async () => {
    if (meetingLink) {
      await navigator.clipboard.writeText(meetingLink);
      snackbar.open("Meeting link copied.");
    }
    handleClose();
  };

  return (
    <Box display="flex" flexDirection="row">
      <IconButton onClick={handleOpen} size="large">
        <MoreHorizIcon htmlColor="#26C9A7" />
      </IconButton>
      <Menu
        id="simple-menu"
        anchorEl={anchorEl}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        keepMounted
        open={Boolean(anchorEl)}
        onClose={handleClose}
      >
        <MenuItem onClick={() => handleOnclick("details")}>
          <InfoIcon className={classes.icon} />
          <Typography
            className={classes.text}
            variant="body2"
            color="textPrimary"
          >
            Appointment Details
          </Typography>
        </MenuItem>
        {isAppointmentActive
          ? [
              <MenuItem
                key="option-menu-edit-item"
                className={classes.menuItem}
                onClick={() => handleOnclick("edit")}
              >
                <EditIcon className={classes.icon} />
                <Typography
                  className={classes.text}
                  variant="body2"
                  color="textPrimary"
                >
                  Edit Appointment
                </Typography>
              </MenuItem>,
              isOnlineAppointment && (
                <MenuItem
                  key="option-menu-copy-meeting-link"
                  className={classes.menuItem}
                  onClick={copyMeetingLink}
                >
                  <ContentCopyRoundedIcon className={classes.icon} />
                  <Typography
                    className={classes.text}
                    variant="body2"
                    color="textPrimary"
                  >
                    Copy Meeting Link
                  </Typography>
                </MenuItem>
              ),
              <MenuItem
                key="option-menu-cancel-item"
                className={classes.menuItem}
                onClick={() => handleOnclick("cancel")}
              >
                <RemoveCircleIcon
                  className={classes.icon}
                  style={{ color: "#FF6262" }}
                />
                <Typography
                  className={classes.text}
                  variant="body2"
                  style={{ color: "#FF6262" }}
                >
                  Cancel Appointment
                </Typography>
              </MenuItem>,
            ]
          : []}
      </Menu>
    </Box>
  );
};

export default OptionMenu;
